import 'chart.js/auto';

import { useEffect, useMemo, useState } from 'react';
import type { ChartData, ChartOptions } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar, Line } from 'react-chartjs-2';
import { Box, Stack, Theme, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Field, Form, Formik, useFormikContext } from 'formik';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useAsyncCallback } from 'react-async-hook';
import { useIntl } from 'react-intl';

import { RepaymentStatisticsResultDto } from 'api/investor-client';
import { investorApiClient } from 'api';
import Card from 'components/card';
import { Select } from 'components/forms';
import { SubmitFormOnChange } from 'components';
import { formatMoney, moveDecimalPoint } from 'utils';

import { barOptions, lineOptions } from './chart-options';
import { getChartDataFromRowStatistics } from './process-row-stat';
import { makeStyles } from '@mui/styles';

dayjs.extend(utc);

interface Props {
  currency: string;
}

interface Values {
  range: { from: Dayjs; to: Dayjs };
  currency: string;
}
type ChartView = 'stacked_bar' | 'line';

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    minHeight: '510px',
  },
}));

export const SyncCurrency = ({ currency }: { currency: string }) => {
  const { values, setFieldValue } = useFormikContext<Values>();

  useEffect(() => {
    if (values.currency !== currency) {
      setFieldValue('currency', currency);
    }
  }, [currency, setFieldValue, values.currency]);

  return null;
};

export const ExpectedRepayments = ({ currency }: Props) => {
  const intl = useIntl();
  const [dataset, setDataset] = useState<RepaymentStatisticsResultDto[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [chartView, setChartView] = useState<ChartView>('stacked_bar');

  const rangeOptions = useMemo(() => {
    return [
      {
        value: {
          from: dayjs.utc().startOf('months'),
          to: dayjs.utc().add(6, 'months').endOf('month'),
        },
        label: intl.formatMessage({ id: 'next_months' }, { nrOfMonths: 6 }),
      },
      {
        value: {
          from: dayjs.utc().startOf('months'),
          to: dayjs.utc().add(12, 'months').endOf('month'),
        },
        label: intl.formatMessage({ id: 'next_months' }, { nrOfMonths: 12 }),
      },
    ];
  }, [intl]);

  const data = useMemo((): ChartData<'bar'> => {
    return getChartDataFromRowStatistics(dataset, intl);
  }, [dataset, intl]);

  const lineData = useMemo((): ChartData<'line'> => {
    const principalData = data.datasets[0]?.data;
    const interestData = data.datasets[1]?.data;

    if (!principalData || !interestData) {
      throw new Error('Expected principal and interest datasets for expected repayments line chart.');
    }

    const summedData = principalData.map((value, index) => {
      return (value as number) + ((interestData[index] as number) ?? 0);
    });

    return {
      labels: data.labels,
      datasets: [{
        label: 'expected_repayments',
        data: summedData,
        borderColor: '#8B2BF4',
        backgroundColor: '#254E8F',
        pointBackgroundColor: '#8B2BF4',
        pointRadius: 4,
        pointHoverRadius: 5,
        tension: 0,
      }],
    };
  }, [data]);

  // const lineOptions = useMemo((): ChartOptions<'line'> => ({
  //   ...options,
  //   plugins: {
  //     ...options.plugins,
  //     datalabels: {
  //       ...options.plugins?.datalabels,
  //       formatter(value: number) {
  //         return formatMoney(value);
  //       },
  //     },
  //   },
  // }), []);

  const getRepaymentStatistics = useAsyncCallback(async (values: Values) => {
    // prettier-ignore
    const { data } = await investorApiClient.investors.investorApiControllerGetRepaymentStatistics({
      from: values.range.from.toISOString(),
      to: values.range.to.toISOString(),
      currency: (values.currency),
    });
    let total = 0;

    data.forEach((d) => {
      d.repayments.forEach((r) => {
        total += r.repayment.principal + r.repayment.interest;
      });
    });

    setDataset(data);
    setTotal(total);
  });

  const classes = useStyles();

  return (
    <Card className={classes.card}>
      <Card.Header variant="condensed" title={intl.formatMessage({ id: 'expected_repayments' })} />
      <Card.Body loadingContent={getRepaymentStatistics.loading}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 4,
          }}
        >
          <Formik<Values>
            initialValues={{
              range: rangeOptions[0].value,
              currency,
            }}
            onSubmit={(v) => {
              getRepaymentStatistics.execute(v);
            }}
          >
            <Form>
              <SyncCurrency currency={currency} />
              <Stack spacing={2}>
              <Field
                  id="range"
                  name="range"
                  sx={{ width: '150px' }}
                  component={Select}
                  options={rangeOptions}
                  variant="filter"
                  disableSelectedClass
                />
                <ToggleButtonGroup
                  color="primary"
                  size="small"
                  exclusive
                  value={chartView}
                  onChange={(_, value: ChartView | null) => {
                    if (value) {
                      setChartView(value);
                    }
                  }}
                >
                  <ToggleButton value="stacked_bar">
                    {intl.formatMessage({ id: 'chart_view_stacked_bar' })}
                  </ToggleButton>
                  <ToggleButton value="line">
                    {intl.formatMessage({ id: 'chart_view_line' })}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
              <SubmitFormOnChange initialSubmit />
            </Form>
          </Formik>

          <Box>
            <Typography variant="h6" component="span">
              {formatMoney(moveDecimalPoint(total, -2))}
            </Typography>
          </Box>
        </Box>
        {chartView === 'stacked_bar'
          ? <Bar options={barOptions} data={data} plugins={[ChartDataLabels]} />
          : <Line options={lineOptions} data={lineData} plugins={[ChartDataLabels]} />}
      </Card.Body>
    </Card>
  );
};
